import { useEffect, useState } from "react";
import { uid } from "../utils/strings";
import { getRemainingMS, getRemainingTime } from "../utils/time";

const INTERVAL_MS: number = 1000;

interface Times {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}
interface ShowTimes {
  showDays: boolean;
  showHours: boolean;
  showMinutes: boolean;
  showSeconds: boolean;
}
export interface Countdown extends ShowTimes, Times {
  isLoading: boolean;
  isComplete: boolean;
}

const intervalJobs: { [uid: string]: Function } = {};
setInterval(() => {
  for (const id in intervalJobs) {
    intervalJobs[id]();
  }
}, INTERVAL_MS);
const addIntervalJob = (job: Function): string => {
  const id = uid();
  intervalJobs[id] = job;
  return id;
};
const delIntervalJob = (id: string) => {
  delete intervalJobs[id];
};

export const useCountdown = (endTimestamp: number): Countdown => {
  if (String(endTimestamp).length < 12) endTimestamp *= 1000;
  const initialRemainingMS: number = getRemainingMS(endTimestamp);
  const [isLoading, setIsLoading] = useState(true);
  const [isComplete, setIsComplete] = useState(initialRemainingMS <= 0);
  const [times, setTimes] = useState<Times>(() =>
    getRemainingTime(initialRemainingMS)
  );

  let intervalID: string;
  useEffect(() => {
    intervalID = addIntervalJob(() => {
      const currentRemainingMS = endTimestamp - Date.now();
      const remainingTime = getRemainingTime(currentRemainingMS);
      if (currentRemainingMS / 1000 > 1) {
        setTimes(remainingTime);
      } else {
        delIntervalJob(intervalID);
        setIsComplete(true);
        setTimes({
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
        });
      }
      setIsLoading(false);
    });
    return () => delIntervalJob(intervalID);
  }, []);

  const showTimes: ShowTimes = {
    showDays: times.days > 0,
    showHours: times.days > 0 || times.hours > 0,
    showMinutes: times.days > 0 || times.hours > 0 || times.minutes > 0,
    showSeconds: true,
  };

  return {
    ...times,
    ...showTimes,
    isLoading,
    isComplete,
  };
};

export default useCountdown;
